// src/pages/ResetPasswordPage.tsx
import React, { useEffect, useState } from 'react';
import { Card, Form, Input, Button, message, Typography, Result } from 'antd';
import { LockOutlined, EyeInvisibleOutlined, EyeTwoTone, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../utils/api';

const { Title, Text } = Typography;

export const ResetPasswordPage: React.FC = () => {
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'form' | 'success' | 'error'>('form');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setErrorMessage('Токен восстановления пароля не найден в ссылке');
    } else {
      // Token exists, show form immediately
      setStatus('form');
    }
  }, [searchParams]);

  const onFinish = async (values: any) => {
    const token = searchParams.get('token');
    if (!token) {
      message.error('Токен восстановления пароля не найден');
      return;
    }

    try {
      setLoading(true);
      const response: any = await authAPI.resetPassword(token, values.password);
      setStatus('success');
      message.success(response.data?.message || 'Пароль успешно изменен!');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error: any) {
      console.error('Reset password error:', error);
      const errorMsg = error.response?.data?.detail || 'Ошибка при сбросе пароля. Попробуйте снова.';
      setStatus('error');
      setErrorMessage(errorMsg);
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };


  if (status === 'success') {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#F0F9F4',
        padding: '20px'
      }}>
        <Card 
          style={{ 
            width: '100%', 
            maxWidth: 500,
            borderRadius: '12px',
            boxShadow: '0 6px 16px rgba(0,0,0,0.08)'
          }}
        >
          <Result
            icon={<CheckCircleOutlined style={{ color: '#7CB342' }} />}
            title="Пароль успешно изменен!"
            subTitle="Теперь вы можете войти в систему с новым паролем."
            extra={[
              <Button 
                type="primary" 
                key="login"
                onClick={() => navigate('/login')}
                size="large"
              >
                Войти в систему
              </Button>
            ]}
          />
        </Card>
      </div>
    );
  }

  if (status === 'error' && !searchParams.get('token')) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#F0F9F4',
        padding: '20px'
      }}>
        <Card 
          style={{ 
            width: '100%', 
            maxWidth: 500,
            borderRadius: '12px',
            boxShadow: '0 6px 16px rgba(0,0,0,0.08)'
          }}
        >
          <Result
            icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
            title="Ошибка"
            subTitle={errorMessage || 'Токен восстановления пароля не найден в ссылке'}
            extra={[
              <Button 
                type="primary" 
                key="forgot"
                onClick={() => navigate('/forgot-password')}
                size="large"
              >
                Запросить новую ссылку
              </Button>,
              <Button 
                key="login"
                onClick={() => navigate('/login')}
                size="large"
              >
                Вернуться к входу
              </Button>
            ]}
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      backgroundColor: '#F0F9F4',
      padding: '20px'
    }}>
      <Card 
        style={{ 
          width: '100%', 
          maxWidth: 500,
          borderRadius: '12px',
          boxShadow: '0 6px 16px rgba(0,0,0,0.08)'
        }}
        title={
          <div style={{ textAlign: 'center' }}>
            <Title level={2} style={{ color: '#263238', marginBottom: 0 }}>Новый пароль</Title>
            <Text type="secondary">Введите новый пароль для вашего аккаунта</Text>
          </div>
        }
      >
        {errorMessage && (
          <div style={{ 
            marginBottom: 20, 
            padding: 12, 
            backgroundColor: '#fff2f0', 
            border: '1px solid #ffccc7',
            borderRadius: 4,
            color: '#ff4d4f'
          }}>
            {errorMessage}
          </div>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
        >
          <Form.Item
            label="Новый пароль"
            name="password"
            rules={[
              { required: true, message: 'Пожалуйста, введите новый пароль' },
              { min: 8, message: 'Пароль должен содержать минимум 8 символов' }
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="Введите новый пароль"
              size="large"
              iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>

          <Form.Item
            label="Подтвердите пароль"
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Пожалуйста, подтвердите пароль' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Пароли не совпадают'));
                },
              }),
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="Подтвердите новый пароль"
              size="large"
              iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              style={{ 
                width: '100%', 
                backgroundColor: '#7CB342',
                borderColor: '#7CB342',
                height: '48px',
                fontSize: '16px',
                fontWeight: '500'
              }}
            >
              Изменить пароль
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Text type="secondary">
            <Button type="link" onClick={() => navigate('/forgot-password')} style={{ padding: 0 }}>
              Запросить новую ссылку
            </Button>
            {' | '}
            <Button type="link" onClick={() => navigate('/login')} style={{ padding: 0 }}>
              Вернуться к входу
            </Button>
          </Text>
        </div>
      </Card>
    </div>
  );
};
